// =============================================
// COMPONENTE - CONCILIACIÓN BANCARIA
// Adaptado para Material-UI
// =============================================

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Checkbox,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  CheckCircle as CheckIcon,
  Pending as PendingIcon,
  TrendingDown as DownIcon,
  TrendingUp as UpIcon,
} from '@mui/icons-material';
import bankReconciliationService from '../../services/bankReconciliationService';

const BankReconciliation = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [transactionTab, setTransactionTab] = useState(0); // 0 = Pendientes, 1 = Procesados
  const [FIAccounts, setFIAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [transactions, setTransactions] = useState([]);
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [processingBulk, setProcessingBulk] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [filters, setFilters] = useState({
    processed: '',
    type: '',
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    loadFIAccounts();
    if (activeTab === 1) {
      loadTransactions();
    }
  }, [activeTab]);

  const loadFIAccounts = async () => {
    try {
      const response = await bankReconciliationService.getActiveFIAccounts();
      setFIAccounts(response.accounts || []);
    } catch (error) {
      console.error('Error al cargar cuentas:', error);
    }
  };

  const loadTransactions = async () => {
    try {
      setLoading(true);
      const response = await bankReconciliationService.getAllTransactions(filters);
      setTransactions(response.transactions || []);
    } catch (error) {
      console.error('Error al cargar transacciones:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ];
      if (!validTypes.includes(selectedFile.type)) {
        setMessage({
          type: 'error',
          text: 'Por favor selecciona un archivo Excel válido (.xlsx o .xls)'
        });
        return;
      }
      setFile(selectedFile);
      setMessage({ type: '', text: '' });
    }
  };

  const handleImport = async () => {
    if (!selectedAccount) {
      setMessage({ type: 'error', text: 'Selecciona una cuenta de la comunidad' });
      return;
    }

    if (!file) {
      setMessage({ type: 'error', text: 'Selecciona un archivo Excel' });
      return;
    }

    try {
      setLoading(true);
      setMessage({ type: '', text: '' });

      const response = await bankReconciliationService.previewImport(
        selectedAccount,
        file
      );

      setPreviewData(response.data);
      setMessage({
        type: 'success',
        text: 'Previsualización cargada. Por favor revisa y confirma.'
      });
      // Do not clear file or account yet

    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Error al importar estado de cuenta'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!previewData) return;

    try {
      setLoading(true);
      const response = await bankReconciliationService.confirmImport(
        previewData.FI_account_id,
        previewData.transactions
      );

      setMessage({
        type: 'success',
        text: `✅ ${response.data.transactions_imported} transacciones importadas y procesadas`
      });

      setFile(null);
      setSelectedAccount('');
      setPreviewData(null);

      setTimeout(() => {
        setActiveTab(1);
      }, 2000);

    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Error al confirmar importación'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelPreview = () => {
    setPreviewData(null);
    setFile(null);
    setMessage({ type: '', text: '' });
  };

  const handleReprocess = async (transactionId) => {
    if (!window.confirm('¿Reprocesar esta transacción?')) return;

    try {
      setLoading(true);

      // Primero intentar obtener sugerencia basada en descripción
      console.log('🔍 Obteniendo sugerencias para transacción:', transactionId);
      const suggestionResponse = await bankReconciliationService.getTransactionSuggestions(transactionId);
      console.log('📊 Respuesta de sugerencias:', suggestionResponse);

      if (suggestionResponse && suggestionResponse.suggestion && suggestionResponse.suggestion.confidence >= 70) {
        console.log('✅ Sugerencia con confianza suficiente, aplicando pago...');
        console.log('📝 Datos a enviar:', {
          owner_account_id: suggestionResponse.suggestion.owner_account_id,
          amount: suggestionResponse.suggestion.transaction_amount,
          confidence: suggestionResponse.suggestion.confidence
        });

        // Aplicar pago directamente usando la sugerencia
        await bankReconciliationService.applyPayment(transactionId, {
          owner_account_id: suggestionResponse.suggestion.owner_account_id,
          amount: suggestionResponse.suggestion.transaction_amount,
          notes: `Auto-aplicado (${suggestionResponse.suggestion.confidence}% confianza) - ${suggestionResponse.suggestion.matched_keywords.join(', ')}`
        });
        setMessage({ type: 'success', text: 'Pago aplicado automáticamente' });
      } else {
        console.log('⚠️ Sin sugerencia o confianza baja, intentando match por keywords...');
        // Si no hay sugerencia con suficiente confianza, intentar match por keywords
        await bankReconciliationService.reprocessTransaction(transactionId);
        setMessage({ type: 'success', text: 'Transacción reprocesada' });
      }

      loadTransactions();
    } catch (error) {
      console.error('❌ Error al reprocesar:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Error al reprocesar'
      });
    } finally {
      setLoading(false);
    }
  };

  // Manejar selección de transacciones
  const handleSelectTransaction = (transactionId) => {
    setSelectedTransactions(prev => {
      if (prev.includes(transactionId)) {
        return prev.filter(id => id !== transactionId);
      } else {
        return [...prev, transactionId];
      }
    });
  };

  const handleSelectAll = () => {
    const unprocessedTransactions = transactions
      .filter(t => !t.is_processed && t.transaction_type === 'entrada')
      .map(t => t.id);

    if (selectedTransactions.length === unprocessedTransactions.length) {
      setSelectedTransactions([]);
    } else {
      setSelectedTransactions(unprocessedTransactions);
    }
  };

  // Aplicar pagos en lote
  const handleBulkApplyPayments = async () => {
    if (selectedTransactions.length === 0) {
      setMessage({ type: 'warning', text: 'Selecciona al menos una transacción' });
      return;
    }

    try {
      setProcessingBulk(true);
      let successCount = 0;
      let failCount = 0;
      const errors = [];

      for (const transactionId of selectedTransactions) {
        try {
          // Obtener sugerencia de match
          const suggestionResponse = await bankReconciliationService.getTransactionSuggestions(transactionId);

          if (suggestionResponse.suggestion && suggestionResponse.suggestion.confidence >= 70) {
            // Aplicar pago automáticamente si la confianza es >= 70%
            await bankReconciliationService.applyPayment(transactionId, {
              owner_account_id: suggestionResponse.suggestion.owner_account_id,
              amount: suggestionResponse.suggestion.transaction_amount,
              notes: `Auto-aplicado (${suggestionResponse.suggestion.confidence}% confianza) - Keywords: ${suggestionResponse.suggestion.matched_keywords.join(', ')}`
            });
            successCount++;
          } else {
            failCount++;
            errors.push(`Transacción ${transactionId}: No se encontró match con suficiente confianza`);
          }
        } catch (error) {
          failCount++;
          errors.push(`Transacción ${transactionId}: ${error.response?.data?.message || error.message}`);
        }
      }

      // Mostrar resultado
      if (successCount > 0) {
        setMessage({
          type: 'success',
          text: `✅ ${successCount} pago(s) aplicado(s) exitosamente${failCount > 0 ? `. ${failCount} fallaron.` : ''}`
        });
      } else {
        setMessage({
          type: 'error',
          text: `❌ No se pudo identificar propietario automáticamente. ${errors[0] || 'Verifica los keywords configurados.'}`
        });
      }

      // Recargar transacciones y limpiar selección
      setSelectedTransactions([]);
      loadTransactions();

    } catch (error) {
      console.error('Error en aplicación masiva:', error);
      setMessage({ type: 'error', text: 'Error al procesar pagos en lote' });
    } finally {
      setProcessingBulk(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-PA', {
      style: 'currency',
      currency: 'PAB'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-PA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          🏦 Conciliación Bancaria Automática
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Importa estados de cuenta y aplica pagos automáticamente
        </Typography>
      </Box>

      {/* Mensaje */}
      {message.text && (
        <Alert
          severity={message.type === 'success' ? 'success' : 'error'}
          sx={{ mb: 3 }}
          onClose={() => setMessage({ type: '', text: '' })}
        >
          {message.text}
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="📤 Importar Estado" />
          <Tab label="📊 Transacciones" />
          <Tab label="📝 Gastos Pendientes" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <Box>
        {/* TAB 0: IMPORTAR */}
        {activeTab === 0 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    📋 Instrucciones
                  </Typography>
                  <Box component="ul" sx={{ pl: 2 }}>
                    <Typography component="li" variant="body2" gutterBottom>
                      El archivo debe ser Excel (.xlsx o .xls)
                    </Typography>
                    <Typography component="li" variant="body2" gutterBottom>
                      Debe tener 3 columnas: Fecha | Descripción | Monto
                    </Typography>
                    <Typography component="li" variant="body2" gutterBottom>
                      Montos positivos = entradas (pagos recibidos)
                    </Typography>
                    <Typography component="li" variant="body2">
                      Montos negativos = salidas (gastos)
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Importar Estado de Cuenta
                </Typography>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Cuenta de la Comunidad</InputLabel>
                  <Select
                    value={selectedAccount}
                    label="Cuenta de la Comunidad"
                    onChange={(e) => setSelectedAccount(e.target.value)}
                  >
                    {FIAccounts.map(account => (
                      <MenuItem key={account.id} value={account.id}>
                        {account.bank_name} - {account.account_number}
                        {account.description && ` (${account.description})`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button
                  component="label"
                  variant="outlined"
                  fullWidth
                  startIcon={<UploadIcon />}
                  sx={{ mb: 2 }}
                >
                  Seleccionar Archivo Excel
                  <input
                    type="file"
                    hidden
                    accept=".xlsx,.xls"
                    onChange={handleFileChange}
                  />
                </Button>

                {file && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    ✅ {file.name} ({(file.size / 1024).toFixed(2)} KB)
                  </Alert>
                )}

                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={loading || !file || !selectedAccount}
                  onClick={handleImport}
                  startIcon={loading ? <CircularProgress size={20} /> : <UploadIcon />}
                >
                  {loading ? 'Cargando...' : 'Previsualizar Importación'}
                </Button>
              </Paper>
            </Grid>

            {/* PREVIEW SECTION */}
            {previewData && (
              <Grid item xs={12}>
                <Paper sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                      👁️ Previsualización ({previewData.count} transacciones)
                    </Typography>
                    <Box>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={handleCancelPreview}
                        sx={{ mr: 1 }}
                        disabled={loading}
                      >
                        Cancelar
                      </Button>
                      <Button
                        variant="contained"
                        color="success"
                        onClick={handleConfirmImport}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : <CheckIcon />}
                      >
                        Confirmar Importación
                      </Button>
                    </Box>
                  </Box>

                  <TableContainer component={Paper} elevation={0} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Fecha</TableCell>
                          <TableCell>Descripción</TableCell>
                          <TableCell>Monto</TableCell>
                          <TableCell>Propietario Sugerido</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {previewData.transactions.map((t, index) => (
                          <TableRow key={index}>
                            <TableCell>{t.Fecha}</TableCell>
                            <TableCell>{t.Descripcion}</TableCell>
                            <TableCell
                              sx={{
                                color: parseFloat(t.Monto) > 0 ? 'success.main' : 'error.main',
                                fontWeight: 'bold'
                              }}
                            >
                              {formatCurrency(t.Monto)}
                            </TableCell>
                            <TableCell>
                              {t.SuggestedOwnerName ? (
                                <Chip
                                  label={t.SuggestedOwnerName}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              ) : (
                                <Typography variant="caption" color="text.secondary">-</Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
            )}
          </Grid>
        )}

        {/* TAB 1: TRANSACCIONES */}
        {activeTab === 1 && (
          <Box>
            {/* Sub-tabs para Pendientes/Procesados */}
            <Paper sx={{ mb: 2 }}>
              <Tabs value={transactionTab} onChange={(e, newValue) => setTransactionTab(newValue)}>
                <Tab label="⏳ Pendientes" />
                <Tab label="✅ Procesados" />
              </Tabs>
            </Paper>
            {/* Filtros */}
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                🔍 Filtros
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Estado</InputLabel>
                    <Select
                      value={filters.processed}
                      label="Estado"
                      onChange={(e) => setFilters({ ...filters, processed: e.target.value })}
                    >
                      <MenuItem value="">Todos</MenuItem>
                      <MenuItem value="true">Procesados</MenuItem>
                      <MenuItem value="false">Sin procesar</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Tipo</InputLabel>
                    <Select
                      value={filters.type}
                      label="Tipo"
                      onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                    >
                      <MenuItem value="">Todos</MenuItem>
                      <MenuItem value="entrada">Entradas</MenuItem>
                      <MenuItem value="salida">Salidas</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="Desde"
                    InputLabelProps={{ shrink: true }}
                    value={filters.start_date}
                    onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="Hasta"
                    InputLabelProps={{ shrink: true }}
                    value={filters.end_date}
                    onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  startIcon={<FilterIcon />}
                  onClick={loadTransactions}
                >
                  Aplicar Filtros
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setFilters({
                    processed: '',
                    type: '',
                    start_date: '',
                    end_date: ''
                  })}
                >
                  Limpiar
                </Button>
              </Box>
            </Paper>

            {/* Tabla de transacciones */}
            <Paper>
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h6">
                    📊 {transactionTab === 0 ? 'Pendientes' : 'Procesados'} ({transactions.filter(t => transactionTab === 0 ? !t.is_processed : t.is_processed).length})
                  </Typography>
                  {selectedTransactions.length > 0 && (
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={processingBulk}
                      onClick={handleBulkApplyPayments}
                      startIcon={processingBulk ? <CircularProgress size={20} /> : <CheckIcon />}
                    >
                      {processingBulk ? 'Procesando...' : `Aplicar Pagos (${selectedTransactions.length})`}
                    </Button>
                  )}
                </Box>
                <Tooltip title="Actualizar">
                  <IconButton onClick={loadTransactions}>
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Box>

              {loading ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <CircularProgress />
                </Box>
              ) : transactions.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    No hay transacciones
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedTransactions.length > 0 && selectedTransactions.length === transactions.filter(t => !t.is_processed && t.transaction_type === 'entrada').length}
                            indeterminate={selectedTransactions.length > 0 && selectedTransactions.length < transactions.filter(t => !t.is_processed && t.transaction_type === 'entrada').length}
                            onChange={handleSelectAll}
                          />
                        </TableCell>
                        <TableCell>Fecha</TableCell>
                        <TableCell>Banco/Cuenta</TableCell>
                        <TableCell>Descripción</TableCell>
                        <TableCell>Propietario Sugerido</TableCell>
                        <TableCell>Tipo</TableCell>
                        <TableCell align="right">Monto</TableCell>
                        <TableCell>Estado</TableCell>
                        <TableCell align="center">Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transactions
                        .filter(t => transactionTab === 0 ? !t.is_processed : t.is_processed)
                        .map((transaction) => (
                          <TableRow key={transaction.id} hover>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedTransactions.includes(transaction.id)}
                                disabled={transaction.is_processed || transaction.transaction_type !== 'entrada'}
                                onChange={() => handleSelectTransaction(transaction.id)}
                              />
                            </TableCell>
                            <TableCell>{formatDate(transaction.transaction_date)}</TableCell>
                            <TableCell>
                              <Typography variant="body2">{transaction.bank_name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {transaction.account_number}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{transaction.description}</Typography>
                              {transaction.notes && (
                                <Typography
                                  variant="caption"
                                  color={(transaction.notes.includes('No se pudo') || transaction.notes.includes('Error')) ? 'error' : 'text.secondary'}
                                  sx={{ fontStyle: 'italic', display: 'block', mt: 0.5 }}
                                >
                                  {transaction.notes}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {transaction.suggested_owner_name ? (
                                <Chip
                                  label={transaction.suggested_owner_name}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              ) : (
                                <Typography variant="caption" color="text.secondary">-</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                icon={transaction.transaction_type === 'entrada' ? <DownIcon /> : <UpIcon />}
                                label={transaction.transaction_type === 'entrada' ? 'Entrada' : 'Salida'}
                                color={transaction.transaction_type === 'entrada' ? 'success' : 'error'}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography
                                variant="body2"
                                fontWeight="bold"
                                color={transaction.amount > 0 ? 'success.main' : 'error.main'}
                              >
                                {formatCurrency(transaction.amount)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                icon={transaction.is_processed ? <CheckIcon /> : <PendingIcon />}
                                label={transaction.is_processed ? 'Procesado' : 'Pendiente'}
                                color={transaction.is_processed ? 'success' : 'warning'}
                              />
                            </TableCell>
                            <TableCell align="center">
                              {!transaction.is_processed && (
                                <Tooltip title="Reprocesar">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleReprocess(transaction.id)}
                                  >
                                    <RefreshIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Box>
        )}

        {/* TAB 2: GASTOS PENDIENTES */}
        {activeTab === 2 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              📝 Gastos Pendientes
            </Typography>
            <Typography color="text.secondary">
              Funcionalidad próximamente...
            </Typography>
          </Paper>
        )}
      </Box>
    </Box >
  );
};

export default BankReconciliation;

